import {
  Add,
  AutoAwesome,
  MoreVert,
  RefreshOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Company } from "../../types";
import { hostname, parseError } from "../../utils";
import IdealCustomerProfileDetail from "./IdealCustomerProfileDetail";
import IdealCustomerProfileDialog from "./IdealCustomerProfileDialog";
import IcpGenerateDialog from "./IdealCustomerProfileGenDialog";

// ICP List Component
export default ({
  currentUser,
  getToken,
  openDialog,
  eventListener,
  companyId,
  company,
  currentPlan,
}: {
  getToken: Function;
  openDialog: Function;
  eventListener: Function;
  currentUser: any | User;
  companyId: string;
  company: Company;
  currentPlan: string;
}) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [icpProfiles, setIcpProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "detail"
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState<any>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalProfiles, setTotalProfiles] = useState(0);

  // Fetch profiles when component mounts
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchProfiles();
      setLoading(false);
    };
    loadData();
  }, [companyId, page, pageSize]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchProfiles = async () => {
    setRefreshing(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/content/profiles`, {
        params: { companyId, page: page + 1, pageSize },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        setIcpProfiles(response.data.items || []);
        setTotalProfiles(response.data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching ICP profiles:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleSaveProfile = async (profileData: any) => {
    setLoading(true);
    try {
      // Ensure profile data has the required structure
      const processedData = {
        ...profileData,
        // Make sure ID is properly handled - convert null to undefined if needed
        id:
          profileData.id && profileData.id !== "null"
            ? profileData.id
            : undefined,

        // Ensure nested objects align with expected database structure
        businessProfile: {
          ...profileData.businessProfile,
          // Handle renamed fields in the UI vs. database
          characteristics:
            profileData.businessProfile?.businessCharacteristics || [],
        },
        goToMarket: {
          ...profileData.goToMarket,
          // Handle renamed fields in the UI vs. database
          content: profileData.goToMarket?.contentStrategies || [],
        },
      };

      const token = await getToken();
      await axios.post(
        `${hostname}/content/profiles`,
        {
          companyId,
          profile: processedData,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Refresh the profiles list
      fetchProfiles();

      // Show success message
      eventListener({
        type: "SET_SUCCESS",
        info: profileData.id
          ? "Profile updated successfully"
          : "Profile created successfully",
      });
    } catch (error) {
      console.error("Error saving ICP profile:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProfile = async (profileId: string) => {
    openDialog(
      "Delete Profile",
      `Are you sure you want to delete this profile?`,
      "Confirm",
      async () => {
        setLoading(true);
        try {
          const token = await getToken();
          await axios.delete(`${hostname}/content/profiles/${profileId}`, {
            params: { companyId },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          // Refresh the profiles list
          fetchProfiles();

          // If we're in detail view and deleting the current profile, go back to grid
          if (viewMode === "detail" && selectedProfile?.id === profileId) {
            handleBackToGrid();
          }
        } catch (error) {
          console.error("Error deleting ICP profile:", error);
          eventListener({
            type: "SET_ERROR",
            error: parseError(error),
          });
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const handleViewProfile = (profile: any) => {
    setSelectedProfile(profile);
    setViewMode("detail");
  };

  const handleBackToGrid = () => {
    setSelectedProfile(null);
    setViewMode("grid");
  };

  // Main Return
  return (
    <Box sx={{ height: "100%", width: "100%", mt: 4 }}>
      {loading && <LinearProgress sx={{ mt: 2 }} />}

      {!loading && viewMode === "grid" && (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setEditorOpen(true)}
                startIcon={<Add />}
                sx={{ mr: 1 }}
              >
                New Profile
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setGenerateOpen(true)}
                startIcon={<AutoAwesome />}
              >
                Generate with AI
              </Button>
            </Box>
          </Box>

          {icpProfiles.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" gutterBottom>
                No Profiles Yet
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Create your first Ideal Customer Profile to better understand
                and target your perfect customers.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setEditorOpen(true)}
                startIcon={<Add />}
              >
                Create First Profile
              </Button>
            </Paper>
          ) : (
            <Paper
              style={{ marginTop: 20, marginBottom: 40 }}
              variant="outlined"
            >
              {refreshing && <LinearProgress />}
              {!refreshing && <div style={{ height: 4 }}></div>}
              <IconButton
                onClick={() => fetchProfiles()}
                style={{ float: "right", marginRight: 5 }}
              >
                <RefreshOutlined />
              </IconButton>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Profile Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Industry</TableCell>
                      <TableCell>Budget Range</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Updated</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {icpProfiles.map((profile: any) => (
                      <TableRow
                        key={profile.id}
                        hover
                        onClick={() => {
                          setSelectedProfile(profile);
                          setEditorOpen(true);
                        }}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {profile.name}
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 250,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {profile.description}
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {profile.industry}
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 120,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {profile.budget}
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {profile.location}
                        </TableCell>
                        <TableCell>
                          {profile.created_date
                            ? moment(profile.created_date).format(
                                "MMMM Do YYYY"
                              )
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {profile.updated_date
                            ? moment(profile.updated_date).format(
                                "MMMM Do YYYY"
                              )
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={(e: any) => {
                              e.stopPropagation();
                              setAnchorEl(e.currentTarget);
                              setSelectedId(profile.id);
                            }}
                          >
                            <MoreVert />
                          </IconButton>
                          <Menu
                            anchorEl={anchorEl}
                            open={selectedId === profile.id}
                            onClose={(e: any) => {
                              e.stopPropagation();
                              setAnchorEl(null);
                              setSelectedId(null);
                            }}
                          >
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewProfile(profile);
                                setAnchorEl(null);
                                setSelectedId(null);
                              }}
                            >
                              View Details
                            </MenuItem>
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditorOpen(true);
                                setSelectedProfile(profile);
                                setAnchorEl(null);
                                setSelectedId(null);
                              }}
                            >
                              Edit
                            </MenuItem>
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteProfile(profile.id);
                                setAnchorEl(null);
                                setSelectedId(null);
                              }}
                            >
                              Delete
                            </MenuItem>
                          </Menu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={totalProfiles}
                  rowsPerPage={pageSize}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {!loading && viewMode === "detail" && (
        <IdealCustomerProfileDetail
          profile={selectedProfile}
          handleBackToGrid={handleBackToGrid}
          setSelectedProfile={setSelectedProfile}
          handleDeleteProfile={handleDeleteProfile}
          setEditorOpen={setEditorOpen}
          setViewMode={setViewMode}
        />
      )}

      {/* Create/Edit Dialog */}
      <IdealCustomerProfileDialog
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setSelectedProfile(null);
        }}
        companyId={companyId}
        company={company}
        onSave={handleSaveProfile}
        existingData={selectedProfile}
        loading={loading}
      />

      {/* Generate with AI Dialog */}
      <IcpGenerateDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        companyId={companyId}
        company={company}
        getToken={getToken}
        eventListener={eventListener}
        onSuccess={fetchProfiles}
      />
    </Box>
  );
};
